import JSZip from 'jszip';

const EMUS_PER_PIXEL = 9525;
const FONT_SIZE_UNITS_PER_PIXEL = 75;
const PPTX_RELATIONSHIP_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

function normalizeArchivePath(path) {
  return path
    .split('/')
    .reduce((parts, part) => {
      if (!part || part === '.') {
        return parts;
      }

      if (part === '..') {
        parts.pop();
        return parts;
      }

      parts.push(part);
      return parts;
    }, [])
    .join('/');
}

function resolveArchivePath(basePath, target) {
  if (!target) {
    return '';
  }

  const baseParts = basePath.split('/');
  baseParts.pop();
  return normalizeArchivePath([...baseParts, ...target.split('/')].join('/'));
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function toArray(collection) {
  return Array.from(collection || []);
}

function getChildElements(node) {
  return toArray(node?.childNodes).filter((child) => child.nodeType === 1);
}

function getChildrenByLocalName(node, localName) {
  return getChildElements(node).filter((child) => child.localName === localName);
}

function getFirstChildByLocalName(node, localName) {
  return getChildrenByLocalName(node, localName)[0] || null;
}

function getDescendantsByLocalName(node, localName) {
  if (!node?.getElementsByTagName) {
    return [];
  }

  return toArray(node.getElementsByTagName('*')).filter((element) => element.localName === localName);
}

function parseXml(xmlText) {
  if (!xmlText || typeof window === 'undefined') {
    return null;
  }

  return new window.DOMParser().parseFromString(xmlText, 'application/xml');
}

function readNumericAttribute(node, attributeName) {
  const value = Number(node?.getAttribute(attributeName) || 0);
  return Number.isFinite(value) ? value : 0;
}

function emuToPixels(value) {
  return Math.max(0, Math.round(readNumericAttribute({ getAttribute: () => value }, '') / EMUS_PER_PIXEL));
}

function attributeNumberToPixels(node, attributeName) {
  return Math.max(0, Math.round(readNumericAttribute(node, attributeName) / EMUS_PER_PIXEL));
}

function fontSizeToPixels(sizeValue) {
  const size = Number(sizeValue || 0);
  if (!Number.isFinite(size) || size <= 0) {
    return 18;
  }

  return Math.max(12, Math.round(size / FONT_SIZE_UNITS_PER_PIXEL));
}

function getShapeFrame(shapeNode) {
  const xfrm = getDescendantsByLocalName(shapeNode, 'xfrm')[0];
  const off = getFirstChildByLocalName(xfrm, 'off');
  const ext = getFirstChildByLocalName(xfrm, 'ext');

  return {
    left: attributeNumberToPixels(off, 'x'),
    top: attributeNumberToPixels(off, 'y'),
    width: Math.max(attributeNumberToPixels(ext, 'cx'), 40),
    height: Math.max(attributeNumberToPixels(ext, 'cy'), 24),
  };
}

function getParagraphAlignment(paragraphNode) {
  const paragraphProperties = getFirstChildByLocalName(paragraphNode, 'pPr');
  const alignment = paragraphProperties?.getAttribute('algn') || '';

  if (alignment === 'ctr') {
    return 'center';
  }

  if (alignment === 'r') {
    return 'right';
  }

  if (alignment === 'just') {
    return 'justify';
  }

  return 'left';
}

function getParagraphLevel(paragraphNode) {
  const paragraphProperties = getFirstChildByLocalName(paragraphNode, 'pPr');
  const level = Number(paragraphProperties?.getAttribute('lvl') || 0);
  return Number.isFinite(level) ? Math.max(0, level) : 0;
}

function getParagraphTextRuns(paragraphNode) {
  return getChildElements(paragraphNode).reduce(
    (accumulator, childNode) => {
      if (childNode.localName === 'br') {
        accumulator.parts.push('<br />');
        return accumulator;
      }

      if (childNode.localName !== 'r' && childNode.localName !== 'fld') {
        return accumulator;
      }

      const text = getDescendantsByLocalName(childNode, 't')
        .map((textNode) => textNode.textContent || '')
        .join('');
      const textProperties = getFirstChildByLocalName(childNode, 'rPr');

      if (textProperties?.getAttribute('sz') && accumulator.fontSize == null) {
        accumulator.fontSize = fontSizeToPixels(textProperties.getAttribute('sz'));
      }

      accumulator.parts.push(escapeHtml(text));
      return accumulator;
    },
    { parts: [], fontSize: null }
  );
}

function renderParagraphs(paragraphNodes) {
  const renderedParagraphs = paragraphNodes
    .map((paragraphNode) => {
      const { parts, fontSize } = getParagraphTextRuns(paragraphNode);
      const paragraphHtml = parts.join('');

      if (!paragraphHtml) {
        return '';
      }

      const paddingLeft = getParagraphLevel(paragraphNode) * 24;
      const alignment = getParagraphAlignment(paragraphNode);

      return `<p class="pptx-preview-paragraph" style="margin:0 0 8px;padding-left:${paddingLeft}px;text-align:${alignment};font-size:${
        fontSize || 18
      }px;">${paragraphHtml}</p>`;
    })
    .filter(Boolean);

  return renderedParagraphs.join('');
}

function renderTextShape(shapeNode) {
  const textBody = getDescendantsByLocalName(shapeNode, 'txBody')[0];
  if (!textBody) {
    return null;
  }

  const paragraphs = getChildrenByLocalName(textBody, 'p');
  const textHtml = renderParagraphs(paragraphs);

  if (!textHtml) {
    return null;
  }

  return {
    ...getShapeFrame(shapeNode),
    html: `<div class="pptx-preview-text-frame">${textHtml}</div>`,
  };
}

function renderTableShape(graphicFrameNode) {
  const tableNode = getDescendantsByLocalName(graphicFrameNode, 'tbl')[0];
  if (!tableNode) {
    return null;
  }

  const rowsHtml = getChildrenByLocalName(tableNode, 'tr')
    .map((rowNode) => {
      const cellsHtml = getChildrenByLocalName(rowNode, 'tc')
        .map((cellNode) => {
          const textBody = getFirstChildByLocalName(cellNode, 'txBody');
          const paragraphs = getChildrenByLocalName(textBody, 'p');
          const cellHtml = renderParagraphs(paragraphs) || '&nbsp;';
          return `<td>${cellHtml}</td>`;
        })
        .join('');

      return `<tr>${cellsHtml}</tr>`;
    })
    .join('');

  if (!rowsHtml) {
    return null;
  }

  return {
    ...getShapeFrame(graphicFrameNode),
    html: `<div class="pptx-preview-table-frame"><table><tbody>${rowsHtml}</tbody></table></div>`,
  };
}

async function readRelationshipTargets(zip, relationshipsPath, sourcePath) {
  const relationshipFile = zip.file(relationshipsPath);
  if (!relationshipFile) {
    return {};
  }

  const relationshipXml = await relationshipFile.async('text');
  const relationshipDoc = parseXml(relationshipXml);

  return getDescendantsByLocalName(relationshipDoc, 'Relationship').reduce((lookup, relationshipNode) => {
    const id = relationshipNode.getAttribute('Id');
    const target = relationshipNode.getAttribute('Target');

    if (id && target) {
      lookup[id] = resolveArchivePath(sourcePath, target);
    }

    return lookup;
  }, {});
}

function getImageMimeType(imagePath) {
  const normalizedPath = imagePath.toLowerCase();

  if (normalizedPath.endsWith('.png')) {
    return 'image/png';
  }

  if (normalizedPath.endsWith('.gif')) {
    return 'image/gif';
  }

  if (normalizedPath.endsWith('.webp')) {
    return 'image/webp';
  }

  return 'image/jpeg';
}

async function renderImageShape(zip, pictureNode, relationshipLookup) {
  const blipNode = getDescendantsByLocalName(pictureNode, 'blip')[0];
  const embedRelationshipId =
    blipNode?.getAttributeNS(PPTX_RELATIONSHIP_NS, 'embed') ||
    blipNode?.getAttribute('r:embed') ||
    blipNode?.getAttribute('embed');
  const imagePath = relationshipLookup?.[embedRelationshipId];

  if (!imagePath) {
    return null;
  }

  const imageFile = zip.file(imagePath);
  if (!imageFile) {
    return null;
  }

  const imageBase64 = await imageFile.async('base64');
  const mimeType = getImageMimeType(imagePath);

  return {
    ...getShapeFrame(pictureNode),
    html: `<img src="data:${mimeType};base64,${imageBase64}" alt="" />`,
  };
}

async function renderSlide(zip, slidePath, slideNumber, slideSize) {
  const slideFile = zip.file(slidePath);
  if (!slideFile) {
    return '';
  }

  const slideXml = await slideFile.async('text');
  const slideDoc = parseXml(slideXml);
  const slideRelationshipsPath = resolveArchivePath(slidePath, `_rels/${slidePath.split('/').pop()}.rels`);
  const relationshipLookup = await readRelationshipTargets(zip, slideRelationshipsPath, slidePath);

  const textFrames = getDescendantsByLocalName(slideDoc, 'sp')
    .map((shapeNode) => renderTextShape(shapeNode))
    .filter(Boolean);
  const tableFrames = getDescendantsByLocalName(slideDoc, 'graphicFrame')
    .map((graphicFrameNode) => renderTableShape(graphicFrameNode))
    .filter(Boolean);
  const imageFrames = (
    await Promise.all(
      getDescendantsByLocalName(slideDoc, 'pic').map((pictureNode) =>
        renderImageShape(zip, pictureNode, relationshipLookup)
      )
    )
  ).filter(Boolean);

  const slideItems = [...textFrames, ...tableFrames, ...imageFrames].sort((firstItem, secondItem) => {
    if (firstItem.top === secondItem.top) {
      return firstItem.left - secondItem.left;
    }

    return firstItem.top - secondItem.top;
  });

  const slideInnerHtml = slideItems
    .map(
      (item) =>
        `<div class="pptx-preview-shape" style="left:${item.left}px;top:${item.top}px;width:${item.width}px;min-height:${item.height}px;">${item.html}</div>`
    )
    .join('');

  return `
    <section class="pptx-preview-slide">
      <div class="pptx-preview-slide-header">Slide ${slideNumber}</div>
      <div class="pptx-preview-slide-canvas" style="width:${slideSize.width}px;height:${slideSize.height}px;">
        ${slideInnerHtml}
      </div>
    </section>
  `;
}

async function getSlidePaths(zip) {
  const presentationFile = zip.file('ppt/presentation.xml');
  if (!presentationFile) {
    return Object.keys(zip.files)
      .filter((filePath) => /^ppt\/slides\/slide\d+\.xml$/i.test(filePath))
      .sort((firstPath, secondPath) => firstPath.localeCompare(secondPath, undefined, { numeric: true }));
  }

  const [presentationXml, relationshipLookup] = await Promise.all([
    presentationFile.async('text'),
    readRelationshipTargets(zip, 'ppt/_rels/presentation.xml.rels', 'ppt/presentation.xml'),
  ]);
  const presentationDoc = parseXml(presentationXml);
  const orderedSlidePaths = getDescendantsByLocalName(presentationDoc, 'sldId')
    .map((slideIdNode) => {
      const relationshipId =
        slideIdNode.getAttributeNS(PPTX_RELATIONSHIP_NS, 'id') ||
        slideIdNode.getAttribute('r:id') ||
        slideIdNode.getAttribute('id');
      return relationshipLookup?.[relationshipId] || null;
    })
    .filter(Boolean);

  if (orderedSlidePaths.length > 0) {
    return orderedSlidePaths;
  }

  return Object.keys(zip.files)
    .filter((filePath) => /^ppt\/slides\/slide\d+\.xml$/i.test(filePath))
    .sort((firstPath, secondPath) => firstPath.localeCompare(secondPath, undefined, { numeric: true }));
}

async function getSlideSize(zip) {
  const presentationFile = zip.file('ppt/presentation.xml');
  if (!presentationFile) {
    return { width: 960, height: 540 };
  }

  const presentationXml = await presentationFile.async('text');
  const presentationDoc = parseXml(presentationXml);
  const slideSizeNode = getDescendantsByLocalName(presentationDoc, 'sldSz')[0];

  if (!slideSizeNode) {
    return { width: 960, height: 540 };
  }

  return {
    width: Math.max(attributeNumberToPixels(slideSizeNode, 'cx'), 480),
    height: Math.max(attributeNumberToPixels(slideSizeNode, 'cy'), 270),
  };
}

export async function parsePptxTemplate(arrayBuffer) {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const [slidePaths, slideSize] = await Promise.all([getSlidePaths(zip), getSlideSize(zip)]);
  const slideHtml = await Promise.all(
    slidePaths.map((slidePath, slideIndex) => renderSlide(zip, slidePath, slideIndex + 1, slideSize))
  );

  return {
    value: `<div class="pptx-preview-document">${slideHtml.join('')}</div>`,
    messages: [],
  };
}
